import { Calendar, FileWarning, MessageCircle, MoreVertical, Search, Settings, UserMinus } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAthletes } from '../../contexts/AthletesContext'
import { useAuth } from '../../contexts/AuthContext'
import { useChatContext } from '../../contexts/ChatContext'
import { FeatureFlagKey } from '../../hooks/useFeatureFlags'
import { theme } from '../../theme/theme'
import type { AthleteSummary } from '../../types/athleteSummary'
import { getAvatarUrl } from '../../utils/avatarUtils'
import { formatDurationMin } from '../../utils/dateUtils'
import { getDisciplineColor } from '../../utils/disciplineColors'
import { getEntryIcon } from '../../utils/disciplineIcons'
import { AthleteProfileModal } from '../coach/AthleteProfileModal'
import { Button } from '../common/Button'
import { Card } from '../common/Card'
import { FeatureFlag } from '../common/FeatureFlag'
import { Tooltip } from '../common/Tooltip'

const SummaryCard = styled(Card)`
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`

const Avatar = styled.div<{ $athleteId: string; $isActive?: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
  overflow: hidden;
  contain: layout;
  view-transition-name: ${({ $athleteId, $isActive }) => ($athleteId && !$isActive ? `athlete-avatar-${$athleteId}` : 'none')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const AthleteInfo = styled.div<{ $athleteId: string; $isActive?: boolean }>`
  flex: 1;

  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.1rem;
    font-weight: 600;
    contain: layout;
    view-transition-name: ${({ $athleteId, $isActive }) => ($athleteId && !$isActive ? `athlete-name-${$athleteId}` : 'none')};
  }

  p {
    margin: 2px 0 0 0;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 0.9rem;
  }
`

const StatusBell = styled.div<{ status: 'good' | 'moderate' | 'attention' | 'warning' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.status === 'good' ? 0.3 : 1)};
  color: ${(props) => {
    switch (props.status) {
      case 'good':
        return theme.colors.textMuted
      case 'moderate':
        return theme.colors.textSecondary
      case 'attention':
        return theme.colors.primary
      case 'warning':
        return theme.colors.error
    }
  }};
  flex-shrink: 0;
`

const ProgressBar = styled.div<{ percentage: number; color?: string }>`
  width: 100%;
  height: 6px;
  background-color: ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  overflow: hidden;
  margin: ${theme.spacing.xs} 0;
  
  &:after {
    content: '';
    display: block;
    height: 100%;
    width: ${(props) => props.percentage}%;
    background-color: ${({ theme, color }) => (color ? color : theme.colors.textSecondary)};
    transition: ${theme.transitions.default};
  }
`

const StatsSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${theme.spacing.md};
`

const StatsHeader = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.xs};
  padding-bottom: ${theme.spacing.md};
`

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
`

const NextRace = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  margin-bottom: ${theme.spacing.md};
  
  .race-info {
    flex: 1;
    
    .race-name {
      font-size: 0.9rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors.text};
      margin: 0;
    }
    
    .race-date {
      font-size: 0.8rem;
      color: ${({ theme }) => theme.colors.textSecondary};
      margin: 0;
    }
  }
`

const LastActivity = styled.div<{ $clickable?: boolean; $isEmpty?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.85rem;
  color: ${theme.colors.textMuted};

  padding: ${theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  margin-bottom: ${theme.spacing.md};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: ${theme.transitions.default};
  min-height: 42px; // Ensures consistent height even when empty

  ${({ $clickable, theme }) =>
    $clickable &&
    `
    &:hover {
      background-color: ${theme.colors.surfaceHover};
      border-color: ${theme.colors.borderHover};
    }
  `}

  ${({ $isEmpty, theme }) =>
    $isEmpty &&
    `
    opacity: 0.5;
    font-style: italic;
  `}

  .activity-type {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: 500;
  }
`

const ActivityDescription = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const ActivityDate = styled.span`
`

const EntryIcon = styled.div<{ $disciplineColor?: string }>`
  display: inline-block;
  color: ${(props) => props.$disciplineColor || props.theme.colors.textSecondary};
`

const CardActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
`

const ChatButtonWrapper = styled.div`
  position: relative;
  width: 100%;
`

const MenuButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`

const MenuContainer = styled.div`
  position: relative;
`

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
`

const MenuItem = styled.button<{ $destructive?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: transparent;
  border: none;
  color: ${({ theme, $destructive }) => ($destructive ? theme.colors.error : theme.colors.text)};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sm};
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`

const ConfirmDialogContent = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  max-width: 400px;
  width: 90%;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

const ConfirmDialogTitle = styled.h3`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`

const ConfirmDialogMessage = styled.p`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.lg} 0;
  line-height: 1.5;
`

const ConfirmDialogActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: flex-end;
`

const ConfirmButton = styled.button<{ $variant: 'primary' | 'danger' }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  border: none;
  font-size: ${theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};

  ${({ $variant, theme }) =>
    $variant === 'primary'
      ? `
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
    &:hover {
      background: ${theme.colors.border};
    }
  `
      : `
    background: ${theme.colors.error};
    color: white;
    &:hover {
      opacity: 0.9;
    }
  `}
`

interface AthleteSummaryCardProps {
  athlete: AthleteSummary
  onClick: () => void
  onAnalyze?: (athleteId: string, athleteName: string) => void
}

const getSleepColor = (avgSleepHours: number | null) => {
  if (avgSleepHours === null) return 'transparent'
  if (avgSleepHours < 6) return theme.colors.error
  if (avgSleepHours < 7.5) return theme.colors.warning
  return theme.colors.success
}

export const AthleteSummaryCard: React.FC<AthleteSummaryCardProps> = ({ athlete, onClick, onAnalyze }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { openChatWithAthlete } = useChatContext()
  const { removeAthlete, refreshAthletes } = useAthletes()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [showEditProfile, setShowEditProfile] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { session } = useAuth()

  // Check if this athlete is currently being viewed in detail
  const isViewingDetail = location.pathname === `/athletes/${athlete.id}`

  // Find the chat with this athlete and check for unread messages
  /*
  const { hasUnread, chatId } = useMemo(() => {
    const athleteChat = chats.find((chat) => chat.athlete_id === athlete.id)

    if (!athleteChat) {
      return { hasUnread: false, chatId: null }
    }

    // Check if the last message was from the athlete (showing a simple indicator)
    // A proper unread count would require tracking read status per user, which we'll add later
    const hasUnread = athleteChat.last_message_sender_id === athlete.id

    return { hasUnread, chatId: athleteChat.id }
  }, [chats, athlete.id])
   */

  const initials = athlete.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()

  // Get avatar URL if available
  const avatarUrl = athlete.avatar ? getAvatarUrl(athlete.avatar) : null
  const weeklyProgress = (athlete.weeklyStats.hoursCompleted / athlete.weeklyStats.hoursPlanned) * 100

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()

    // Reset time to midnight to compare calendar days, not 24-hour periods
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    const diffTime = todayOnly.getTime() - dateOnly.getTime()
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    return `${diffDays} days ago`
  }

  const handleAnalyzeClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onAnalyze) {
      onAnalyze(athlete.id, athlete.name)
    }
  }

  const handleCalendarClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick()
  }

  const handleChatClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    openChatWithAthlete(athlete.id)
  }

  const handleLastActivityClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (athlete.lastActivity?.workoutId) {
      navigate(`/athletes/${athlete.id}/workouts/${athlete.lastActivity.workoutId}`)
    }
  }

  const handleEditProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(false)
    setShowEditProfile(true)
  }

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(false)
    setShowRemoveConfirm(true)
  }

  const handleConfirmRemove = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const result = await removeAthlete(athlete.id)
      if (!result.success) {
        console.error('Failed to remove athlete:', result.error)
        // TODO: Show error notification to user
      }
      setShowRemoveConfirm(false)
    } catch (error) {
      console.error('Failed to remove athlete:', error)
      // TODO: Show error notification to user
    }
  }

  const handleCancelRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowRemoveConfirm(false)
  }

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(!menuOpen)
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  const EntryTypeIconComponent = getEntryIcon(athlete.lastActivity?.activityType)

  return (
    <SummaryCard>
      <CardHeader>
        <Avatar $athleteId={athlete.id} $isActive={isViewingDetail}>{avatarUrl ? <img src={avatarUrl} alt={athlete.name} /> : initials}</Avatar>
        <AthleteInfo $athleteId={athlete.id} $isActive={isViewingDetail}>
          <h3>{athlete.name}</h3>
        </AthleteInfo>
        {athlete.hasDeviceConnections === false && (
          <Tooltip content="No device connected. Please remind the athlete to connect a device.">
            <StatusBell status={'warning'}>
              <FileWarning size={16} />
            </StatusBell>
          </Tooltip>
        )}
        <MenuContainer ref={menuRef}>
          <MenuButton onClick={handleMenuClick} title="More options">
            <MoreVertical size={20} />
          </MenuButton>
          {menuOpen && (
            <DropdownMenu>
              <MenuItem onClick={handleEditProfileClick}>
                <Settings />
                Edit Profile
              </MenuItem>
              <MenuItem $destructive onClick={handleRemoveClick}>
                <UserMinus />
                Remove Athlete
              </MenuItem>
            </DropdownMenu>
          )}
        </MenuContainer>
      </CardHeader>

      {athlete.nextRace && (
        <NextRace>
          <Calendar size={16} color={theme.colors.textMuted} />
          <div className="race-info">
            <p className="race-name">{athlete.nextRace.name}</p>
            <p className="race-date">
              {athlete.nextRace.date} • {athlete.nextRace.distance}
            </p>
          </div>
        </NextRace>
      )}

      <LastActivity
        $clickable={!!athlete.lastActivity?.workoutId}
        $isEmpty={!athlete.lastActivity}
        onClick={athlete.lastActivity?.workoutId ? handleLastActivityClick : undefined}
      >
        {athlete.lastActivity ? (
          <>
            <div>
              <EntryIcon $disciplineColor={getDisciplineColor(athlete.lastActivity.activityType)}>
                <EntryTypeIconComponent size={14} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
              </EntryIcon>
              <ActivityDescription>{athlete.lastActivity.description}</ActivityDescription>
            </div>
            <ActivityDate>{formatDate(athlete.lastActivity.date)}</ActivityDate>
          </>
        ) : (
          <div>No recent activity</div>
        )}
      </LastActivity>

      {/*
      <MetricsGrid>
        <MetricItem>
          <p className="label">Body Battery</p>
          <p className="value">{athlete.status.recovery}%</p>
        </MetricItem>
        <MetricItem>
          <p className="label">Stress</p>
          <p className="value">{athlete.status.training}%</p>
        </MetricItem>
        <MetricItem>
          <p className="label">Avg. Sleep Score</p>
          <p className="value">{athlete.status.sleep}</p>
        </MetricItem>
      </MetricsGrid>
      */}

      <StatsSection>
        <StatsHeader>Last 7 Days</StatsHeader>

        <StatRow>
          <span>{athlete.weeklyStats.workoutsCompleted} Workouts</span>
          <span>{formatDurationMin(athlete.weeklyStats.hoursCompleted * 60)}</span>
        </StatRow>
        <ProgressBar percentage={weeklyProgress} />

        <StatRow>
          <span>Avg. Sleep</span>
          <span>
            {athlete.weeklyStats.avgSleepHours ? formatDurationMin(athlete.weeklyStats.avgSleepHours * 60) : 'n/a'}
          </span>
        </StatRow>
        <ProgressBar
          percentage={((athlete.weeklyStats.avgSleepHours ?? 0) / 7) * 100}
          color={getSleepColor(athlete.weeklyStats.avgSleepHours)}
        />
      </StatsSection>

      <CardActions>
        <Button variant="secondary" size="lg" onClick={handleCalendarClick} style={{ width: '100%' }}>
          <Calendar size={16} />
          Calendar
        </Button>
        <ChatButtonWrapper>
          <Button
            variant="secondary"
            size="lg"
            onClick={handleChatClick}
            style={{ width: '100%' }}
            disabled={athlete.id === session?.user?.id}
          >
            <MessageCircle size={16} />
            Chat
          </Button>
          {/*hasUnread && <UnreadBadge>●</UnreadBadge>*/}
        </ChatButtonWrapper>
        <FeatureFlag flag={FeatureFlagKey.CoachAnalysis}>
          {onAnalyze && (
            <Button variant="secondary" size="lg" onClick={handleAnalyzeClick} style={{ width: '100%' }}>
              <Search size={16} />
              Start Analysis
            </Button>
          )}
        </FeatureFlag>
      </CardActions>

      {/* Edit Profile Modal */}
      <AthleteProfileModal
        isOpen={showEditProfile}
        onClose={() => setShowEditProfile(false)}
        athleteId={athlete.id}
        athleteName={athlete.name}
        onProfileUpdated={refreshAthletes}
      />

      {/* Remove Confirmation Dialog */}
      {showRemoveConfirm && (
        <ConfirmDialog onClick={handleCancelRemove}>
          <ConfirmDialogContent onClick={(e) => e.stopPropagation()}>
            <ConfirmDialogTitle>Confirm Remove Athlete Connection</ConfirmDialogTitle>
            <ConfirmDialogMessage>
              This will remove your connection as coach of {athlete.name}. It will not delete any data associated with
              this athlete. To undo, the athlete must add you as coach again.
            </ConfirmDialogMessage>
            <ConfirmDialogActions>
              <ConfirmButton $variant="primary" onClick={handleCancelRemove}>
                Cancel
              </ConfirmButton>
              <ConfirmButton $variant="danger" onClick={handleConfirmRemove}>
                Remove Athlete Connection
              </ConfirmButton>
            </ConfirmDialogActions>
          </ConfirmDialogContent>
        </ConfirmDialog>
      )}
    </SummaryCard>
  )
}
